import { useState } from 'react';
import { Link } from 'react-router-dom';
import './v2/expert-scene.css';
import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Check,
  ChevronRight,
  FileText,
  Layers3,
  MessageSquare,
  ShieldCheck,
  Workflow,
} from 'lucide-react';
import { DEMO_CTA_HREF } from '@/config';
import { HeroExperience } from './v2/HeroExperience';

const scenarios = [
  {
    name: 'Evaluate',
    question: 'Can our regional teams manage approvals independently?',
    answer:
      'Yes. Let’s look at how regional approval rules work, with central visibility for your operations team.',
    flow: 'Regional approval routing',
    step: 'Route by region',
    rows: [
      ['Europe', 'Regional lead', 'Central visibility'],
      ['Asia Pacific', 'Regional lead', 'Central visibility'],
    ],
    brief: 'Multi-region operations',
    detail: 'Needs local approval ownership with central oversight.',
    insight: 'Regional autonomy is a key evaluation requirement.',
    next: 'Review approval requirements',
  },
  {
    name: 'Onboard',
    question: 'How do I set up approvals for our first region?',
    answer:
      'Start with your region, assign its approval owner, then review the routing rule before you enable it.',
    flow: 'Set up your first region',
    step: 'Assign an owner',
    rows: [
      ['Europe', 'Select regional lead', 'Review routing'],
      ['Central team', 'Operations admin', 'Keep visibility'],
    ],
    brief: 'First-region rollout',
    detail: 'Guided setup for a regional operations owner.',
    insight: 'Approval ownership needs to be clear during setup.',
    next: 'Confirm the rollout owner',
  },
  {
    name: 'Support',
    question: 'Why did this request go to our central team?',
    answer:
      'The regional owner has not been assigned. The fallback rule sends unassigned requests to central operations.',
    flow: 'Understand approval routing',
    step: 'Inspect fallback rule',
    rows: [
      ['Regional owner', 'Not assigned', 'Use fallback'],
      ['Fallback team', 'Central operations', 'Request received'],
    ],
    brief: 'Approval routing question',
    detail: 'Missing regional owner triggered the central fallback.',
    insight: 'Make fallback behaviour easier to discover in setup.',
    next: 'Assign a regional owner',
  },
];

export function ProductIntelligenceScene() {
  const [selected, setSelected] = useState(0);
  const scene = scenarios[selected];
  return (
    <div className="expert-scene">
      <div className="expert-toolbar">
        <div className="expert-workspace">
          <img src="/fox-icon.svg" alt="" width="24" height="24" />
          <strong>Product intelligence</strong>
          <ChevronRight size={14} />
          <span>Northstar</span>
        </div>
        <span className="scene-disclaimer">Illustrative experience</span>
      </div>
      <div className="expert-body">
        <aside className="expert-foundation">
          <p className="tiny-label">THE PRODUCT FOUNDATION</p>
          <h3>Expertise, connected.</h3>
          <p className="foundation-description">
            The knowledge behind every interaction.
          </p>
          <div className="foundation-source">
            <Workflow size={18} />
            <div>
              <strong>Product workflows</strong>
              <span>Regional approvals</span>
            </div>
            <Check size={14} />
          </div>
          <div className="foundation-source">
            <BookOpen size={18} />
            <div>
              <strong>Approved knowledge</strong>
              <span>Roles & permissions</span>
            </div>
            <Check size={14} />
          </div>
          <div className="foundation-source">
            <MessageSquare size={18} />
            <div>
              <strong>Customer context</strong>
              <span>Multi-region operations</span>
            </div>
            <Check size={14} />
          </div>
          <div className="foundation-control">
            <ShieldCheck size={16} />
            <span>
              Shaped by your team.
              <br />
              Grounded in your product.
            </span>
          </div>
        </aside>
        <div className="expert-interaction">
          <div
            className="expert-switch"
            role="group"
            aria-label="Explore a product expertise scenario"
          >
            {scenarios.map((item, i) => (
              <button
                key={item.name}
                onClick={() => setSelected(i)}
                aria-pressed={i === selected}
              >
                {item.name}
                <ArrowUpRight size={13} />
              </button>
            ))}
          </div>
          <div
            className="expert-conversation"
            aria-live="polite"
            aria-atomic="true"
          >
            <div className="customer-message">
              <span className="customer-avatar">JD</span>
              <div>
                <span className="tiny-label">CUSTOMER QUESTION</span>
                <p>{scene.question}</p>
              </div>
            </div>
            <div className="foxet-answer">
              <img src="/fox-icon.svg" alt="" width="24" height="24" />
              <div>
                <strong>Foxet product expert</strong>
                <p>{scene.answer}</p>
                <span className="answer-source">
                  <BookOpen size={12} /> Roles & permissions <span>·</span>{' '}
                  Product workflow
                </span>
              </div>
            </div>
            <div className="expert-product-view">
              <div className="product-view-header">
                <span className="northstar-symbol">N</span>
                <strong>Northstar</strong>
                <span>Approval settings</span>
              </div>
              <div className="product-view-title">
                <div>
                  <span className="tiny-label">RELEVANT PRODUCT WORKFLOW</span>
                  <h4>{scene.flow}</h4>
                </div>
                <span className="flow-code">F-02</span>
              </div>
              <div className="approval-table">
                <div className="approval-table-head">
                  <span>Rule</span>
                  <span>Owner</span>
                  <span>Behaviour</span>
                </div>
                {scene.rows.map((row) => (
                  <div key={row[0]}>
                    {row.map((cell) => (
                      <span key={cell}>{cell}</span>
                    ))}
                  </div>
                ))}
              </div>
              <div className="product-view-step">
                <span>
                  <Check size={13} />
                  {scene.step}
                </span>
                <span>
                  Guided by Foxet <ArrowRight size={13} />
                </span>
              </div>
            </div>
          </div>
        </div>
        <aside
          className="expert-outcomes"
          aria-live="polite"
          aria-atomic="true"
        >
          <p className="tiny-label">INTELLIGENCE FOR YOUR TEAM</p>
          <div className="outcome-block">
            <span className="outcome-label">
              <FileText size={15} /> GTM BRIEF
            </span>
            <h3>{scene.brief}</h3>
            <p>{scene.detail}</p>
            <div className="outcome-next">
              <ArrowUpRight size={14} />
              {scene.next}
            </div>
          </div>
          <div className="outcome-block">
            <span className="outcome-label">
              <Layers3 size={15} /> PRODUCT SIGNAL
            </span>
            <p className="signal-copy">{scene.insight}</p>
            <span className="signal-source">Connected to the conversation</span>
          </div>
          <div className="outcome-footnote">
            One interaction.
            <br />
            <strong>Shared understanding.</strong>
          </div>
        </aside>
      </div>
      <div className="expert-status">
        <span>
          <i />
          Product knowledge → Customer experience → Team intelligence
        </span>
        <span>
          Explore the scenarios above <ArrowUpRight size={12} />
        </span>
      </div>
    </div>
  );
}
// The four-statement rail moved to v2/StatementRail.tsx on 2026-10-22; it now renders
// after "One brain" (owner: two bands under the hero read as one heavy block).

export function Hero() {
  return (
    <section className="fx-hero">
      <div className="fx-hero-wash" aria-hidden="true" />
      <div className="container fx-hero-grid">
        <div className="fx-hero-copy">
          <p className="fx-eyebrow fx-ld fx-d1">
            <span className="fx-dot" aria-hidden="true" />
            Your product expertise. Whenever it matters.
          </p>
          <h1 className="fx-hero-h1 fx-ld fx-d2">
            <span>Intelligence infrastructure for</span>
            <span>your Product &amp; GTM</span>
          </h1>
          <p className="fx-hero-sub fx-ld fx-d3">
            Product expertise for every conversation. Intelligence for every
            team.
          </p>
          <div className="button-row fx-ld fx-d4">
            <a className="button button-primary fx-live-cta" href={DEMO_CTA_HREF}>
              <i className="fx-live-dot" aria-hidden="true" />
              Try Live Demo <ArrowRight size={16} aria-hidden="true" />
            </a>
            <a className="button button-secondary" href="https://app.foxet.ai/talk/foxet-ai-v1-1">
              Talk to Sales <ArrowRight size={16} aria-hidden="true" />
            </a>
          </div>
        </div>
        <HeroExperience />
      </div>
    </section>
  );
}
